import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseApiService, PaginatedResults } from '@bo/ng-api';
import {
  CurrentSubscription,
  SubscriptionDetail,
  SubscriptionInvoice,
  SubscriptionCancel,
  SubscriptionCancelReasons,
  SubscriptionPayload,
  SubscriptionProRata,
  SubscriptionCancelResponse,
  SubscriptionFrequency,
  SubscriptionPaymentRequest,
  SubscriptionPaymentResponse,
} from '../../../_interfaces/subscription.interface';
import { map, Observable, tap } from 'rxjs';
import { FileDownloaderService } from '../../file-downloader.service';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionApiService extends BaseApiService {
  protected override http: HttpClient;
  readonly fileDownloader = inject(FileDownloaderService);

  override baseUrl: string = environment.apiRoot;
  URL_SUBSCRIPTION: string = 'subscription';
  URL_SUBSCRIPTION_PACKAGE: string = 'subscription/package';
  URL_SUBSCRIPTION_CURRENT: string = 'subscription/current';
  URL_SUBSCRIPTION_PACKAGE_UPGRADE_PACKAGES: string = 'subscription/package/upgrade-packages';
  URL_SUBSCRIPTION_UPGRADE: string = 'subscription/upgrade';
  URL_SUBSCRIPTION_PRORATA: string = 'subscription/pro-rata';
  URL_SUBSCRIPTION_PAYMENT: string = 'subscription/payment';

  URL_SUBSCRIPTION_INVOICE: string = 'subscription/invoice';
  URL_SUBSCRIPTION_CANCEL: string = 'subscription/cancel';
  URL_SUBSCRIPTION_CANCEL_REASON: string = 'subscription/cancel-reasons';
  URL_RENDER_INVOICE: string = 'render-invoice'; // relative to URL_SUBSCRIPTION_INVOICE

  constructor() {
    const http = inject(HttpClient);

    super(http);

    this.http = http;
  }

  /**
   * Get all subscription packages
   *
   * @return Observable<SubscriptionDetail[]>
   */
  getSubscriptionPackages(): Observable<SubscriptionDetail[]> {
    return this.http.get<Array<SubscriptionDetail>>(this.getUrl(this.URL_SUBSCRIPTION_PACKAGE, true));
  }

  /**
   * Get active/current subscription package
   *
   * @return Observable<CurrentSubscription>
   */
  getCurrentSubscription(): Observable<CurrentSubscription> {
    return this.http.get<CurrentSubscription>(this.getUrl(this.URL_SUBSCRIPTION_CURRENT, true));
  }

  /**
   * Get all available upgrade subscription packages
   *
   * @return Observable<SubscriptionDetail[]>
   */
  getUpgradeSubscription(): Observable<SubscriptionDetail[]> {
    return this.http.get<SubscriptionDetail[]>(this.getUrl(this.URL_SUBSCRIPTION_PACKAGE_UPGRADE_PACKAGES, true));
  }

  /**
   * Get all subscription invoices
   *
   * @return Observable<PaginatedResults<Array<SubscriptionInvoice>>>
   */
  getSubscriptionInvoices(queryParams: HttpParams): Observable<PaginatedResults<SubscriptionInvoice>> {
    const url = this.getUrl(this.URL_SUBSCRIPTION_INVOICE, true);
    return this.http.get<PaginatedResults<SubscriptionInvoice>>(url, { observe: 'response', params: queryParams }).pipe(
      map((res) => {
        return new PaginatedResults<SubscriptionInvoice>(res);
      }),
    );
  }

  /**
   * Download subscription invoice PDF
   *
   * @param invoiceId
   * @return Observable<Blob>
   */
  downloadSubscriptionInvoice(invoiceId: string): Observable<Blob> {
    let params = new HttpParams();
    params = params.append('format', 'pdf');
    const url = this.getUrl([this.URL_SUBSCRIPTION_INVOICE, invoiceId, this.URL_RENDER_INVOICE], true);
    return this.http
      .get(url, {
        responseType: 'blob',
        params: params,
        headers: {
          'Accept': 'application/pdf',
        },
      })
      .pipe(
        tap((blob: Blob) => {
          this.fileDownloader.download(blob, `Subscription_Invoice_${invoiceId}`);
        }),
      );
  }

  /**
   * Create/Select a subscription package
   *
   * @params data
   * @return Observable<SubscriptionPayload>
   */
  selectSubscriptionPackage(data: SubscriptionPayload): Observable<SubscriptionPayload> {
    return this.http
      .post(this.getUrl(this.URL_SUBSCRIPTION, true), data)
      .pipe(map((data) => data as SubscriptionPayload));
  }

  /**
   * Upgrade to a new subscription package
   *
   * @params data
   * @return Observable<SubscriptionPayload>
   */
  selectSubscriptionUpgradePackage(data: SubscriptionPayload): Observable<SubscriptionPayload> {
    return this.http.put<SubscriptionPayload>(this.getUrl(this.URL_SUBSCRIPTION_UPGRADE, true), data);
  }

  /**
   * Cancel a subscription package
   *
   * @params data
   * @return Observable<SubscriptionCancelResponse>
   */
  cancelSubscription(data: SubscriptionCancel): Observable<SubscriptionCancelResponse> {
    const url = this.getUrl(this.URL_SUBSCRIPTION_CANCEL, true);
    return this.http.post<SubscriptionCancelResponse>(url, data);
  }

  /**
   * Get cancel a subscription reasons
   *
   * @return Observable<SubscriptionCancelReasons>
   */
  getSubscriptionCancelReasons(): Observable<SubscriptionCancelReasons[]> {
    return this.http.get<Array<SubscriptionCancelReasons>>(this.getUrl(this.URL_SUBSCRIPTION_CANCEL_REASON, true));
  }

  /**
   * Get subscription pro-rata amount
   *
   * @params data
   * @return Observable<SubscriptionProRata>
   */
  getSubscriptionProRata(packageId: number, frequency: SubscriptionFrequency): Observable<SubscriptionProRata> {
    const url = this.getUrl(this.URL_SUBSCRIPTION_PRORATA, true);
    const params = new HttpParams().set('frequency', frequency).set('package', packageId);
    return this.http.get<SubscriptionProRata>(url, { params });
  }

  /**
   * Get subscription payment details for Payfast
   *
   * @params data
   * @return Observable<SubscriptionPaymentRequest>
   */
  requestSubscriptionPaymentDetails(data: SubscriptionPaymentRequest): Observable<SubscriptionPaymentResponse> {
    const url = this.getUrl(this.URL_SUBSCRIPTION_PAYMENT, true);
    return this.http.post<SubscriptionPaymentResponse>(url, data);
  }
}
